import React, { useEffect, useMemo, useState } from "react";
// Material Styling
import { FormControl, Theme } from "@mui/material";
import { default as MaterialTextField, TextFieldProps as MaterialTextFieldProps } from "@mui/material/TextField";
import classNames from "classnames";
// Formik imports
import { FieldMetaProps } from "formik";
import { FieldError } from "./FieldError";

interface ITextProps {
  //InputProps?: any; // <- material input wrapper component
  //inputProps?: any; // <- actual input element
  className?: string;
  onChangeDelay?: number;
  meta?: Partial<FieldMetaProps<any>>;
  id?: string; // Override use of input name for id assigned.
  labelSx?: any;
}

export type ITextFieldProps = ITextProps & MaterialTextFieldProps;

export const TextFieldStyles = (theme: Theme, labelSx: any): any => ({
  root: {
    display: "flex",
    flexDirection: "column",
    margin: "5px",
    flexWrap: "wrap",
    width: "calc(100% - 10px)",
    "&.split_label_input": {
      width: "calc(100% - 10px)",
      "& .input_control": {
        "& label, & .input-base": {
          width: "50%",
          maxHeight: "24px",
        },
      },
    },
    "&.no_arrows .input_control div.input-base": {
      "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
        WebkitAppearance: "none",
        margin: 0,
      },
      "& input[type=number]": {
        MozAppearance: "textfield",
      },
    },
    "&.auto_width .input_control label": { width: "auto" },
    "& .hideMuiLabel": {
      "& label": {
        display: "none !important",
      },
    },

    "& .input_control": {
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      "& label": {
        position: "relative",
        textAlign: "right",
        transform: "none",
        transition: "none",
        color: theme.custom.forms.pristineColor,
        fontSize: 12,
        lineHeight: "12px",
        margin: "5px 10px auto auto",
        overflow: "unset",
        ...labelSx,
      },
      "& div": {
        margin: 0,
        display: "inline-flex",
        flexWrap: "wrap",
        alignItems: "center",
        flexDirection: "row",
        "&.input-base": {
          padding: "0px 8px",
          minHeight: "2px",
          borderRadius: "2px",
          border: "1px solid " + theme.custom.forms.inputBorder,
          color: theme.custom.forms.inputText,
          backgroundColor: theme.custom.forms.inputBack,
          transition: theme.transitions.create(["border-color", "color"]),
          "&.MuiInputBase-fullWidth": {
            flexGrow: 1,
            width: "auto",
          },
          "&.MuiInputBase-multiline": {
            padding: "0px 0px 0px 8px",
          },
          "&:before": { display: "none" },
          "&:hover": {
            borderColor: theme.custom.forms.pristineColor,
          },
          "&[disabled]": {
            opacity: 0.5,
            borderStyle: "dotted",
            userSelect: "none",
          },
          "& .MuiInputAdornment-root p": {
            fontSize: 14,
            fontWeight: 300,
          },
          "& input": {
            fontFamily: "Heebo, sans-serif",
            fontWeight: 300,
            fontSize: 12,
            margin: 0,
            padding: 0,
            width: "100%",
            maxWidth: "100%",
            minHeight: "20px",
            position: "relative",
            transition: theme.transitions.create(["color"]),
            boxSizing: "border-box",
            "&::placeholder, &::-webkit-input-placeholder": {
              opacity: 1,
              color: theme.custom.forms.labels,
            },
          },
          "& textarea": {
            fontSize: 12,
            fontWeight: 300,
            padding: "2px 0px",
            overflowY: "auto !important", // investigate why material-ui is setting this to hidden with inline style.
          },
        },
      },
    },
    "&.locked .input_control div": {
      opacity: 0.8,
      userSelect: "none",
      pointerEvents: "none",
    },
    "&.disabled .input_control div": {
      opacity: 0.5,
      userSelect: "none",
      pointerEvents: "none",
    },
    "&.dirty .input_control": {
      "& label": {
        color: theme.custom.forms.dirtyColor,
      },
      "& div.input-base": {
        border: "1px solid " + theme.custom.forms.dirtyColor,
      },
    },
    "&.focused .input_control": {
      "& label": {
        color: theme.custom.forms.focusedColor,
      },
      "& div.input-base": {
        border: "1px solid " + theme.custom.forms.pristineColor,
      },
    },
    // Keep `error` last: these three blocks have equal CSS strength, so the one written
    // last wins. That is what makes the red border beat the green dirty/focused ones.
    "&.error .input_control": {
      "& label": {
        color: theme.custom.forms.errorColor,
      },
      "& div.input-base": {
        border: "1px solid " + theme.custom.forms.errorColor,
        "&:hover": {
          borderColor: theme.custom.forms.errorColor,
        },
      },
    },
    "& .error-message": {
      maxWidth: 300,
      padding: "0px 5px",
      fontWeight: 300,
      color: theme.palette.error.main,
    },
  },
  dateTime: {
    backgroundColor: theme.custom.forms.inputBack,
    border: "1px solid " + theme.custom.forms.inputBorder,
    "&:hover": {
      borderColor: theme.custom.forms.pristineColor,
    },
    "& input": {
      color: theme.custom.forms.inputText,
      fontFamily: "Heebo, sans-serif",
      fontWeight: 300,
      fontSize: 12,
      padding: "2px 0px 0px 5px",
      margin: "1px 0px",
      width: "75px",
    },
    "& svg": {
      width: ".75rem",
      height: ".75rem",
    },
  },
  mask: {
    "& input": {
      color: theme.custom.forms.inputText,
      fontFamily: "Heebo, sans-serif",
      fontWeight: 300,
      fontSize: 12,
      padding: "2px 0px 0px 5px",
      margin: "1px 0px",
      width: "50px",
      backgroundColor: theme.custom.forms.inputBack,
      border: "1px solid " + theme.custom.forms.inputBorder,
      "&:hover": {
        borderColor: theme.custom.forms.pristineColor,
      },
    },
    "& svg": {
      width: ".75rem",
      height: ".75rem",
    },
  },
});

// TEXTFIELD COMPONENT
export const TextField = (props: ITextFieldProps): JSX.Element => {
  const {
    label,
    name,
    onFocus,
    onBlur,
    onChange,
    onKeyDown,
    value,
    onChangeDelay,
    InputProps,
    inputProps,
    className,
    disabled,
    meta,
    id,
    labelSx,
    type = "text",
  } = props;

  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [typingTimer, setTypingTimer] = useState<any>(null);
  const [internalValue, setInternalValue] = useState<string | number>("");

  // Track if formik value is dirty.
  const dirty: boolean = useMemo(() => {
    if (!meta) return false;
    const { initialValue, touched } = meta;
    return touched && value !== initialValue ? true : false;
  }, [meta, value]);

  // Clear timer on unmount of component.
  useEffect(() => () => clearTimeout(typingTimer), [typingTimer]);

  // If value is changed from outside, update internal controlled value
  useEffect(() => {
    // Debug.log('outside change: ', value)
    const newValue = value === undefined || value === null ? "" : value;
    setInternalValue(newValue as string | number);
  }, [value]);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setIsFocused(true);
    // Uses props onfocus (as formik doesn't supply one)
    if (onFocus && typeof onFocus === "function") {
      onFocus(e);
    }
  };

  // Custom blur handler for bug workaround due to input value
  // resetting when using search feature onBlur.
  const handleBlur = (e: React.FocusEvent<Element>): void => {
    e.persist();
    setIsFocused(false);
    setTimeout(() => {
      if (onBlur && typeof onBlur === "function") {
        onBlur(e as React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>);
      }
      if (type === "number") {
        //@ts-ignore
        onChange(e);
      }
    }, 1);
  };

  // If value is modified by the input component, update the parent controller after a slight delay.
  const handleInputOnchange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    if (!event?.target) {
      return;
    }
    const delay = onChangeDelay !== undefined ? onChangeDelay : 500;
    event.persist();
    setInternalValue(event.target.value);
    // Reset timer onChange and restart it.
    if (typingTimer) clearTimeout(typingTimer);
    if (type === "number") return;
    setTypingTimer(
      setTimeout(() => {
        // Fire off the supplied onChange from props and not the formik field one (it breaks as it wants an event and not a value)
        if (onChange && typeof onChange === "function") {
          onChange(event);
        }
      }, delay)
    );
  };

  const hasError = meta?.error ? true : false;
  return (
    <FormControl
      className={classNames(
        className,
        disabled && "disabled",
        isFocused && "focused",
        dirty && "dirty",
        hasError && "error"
      )}
      sx={(theme) => TextFieldStyles(theme, labelSx).root}
      error={hasError}
    >
      <MaterialTextField
        {...props}
        className={classNames("input_control", !label && "hideMuiLabel")}
        inputProps={{ id: id ? id : name ? name + "_input" : "", ...inputProps }}
        label={!!label && label}
        disabled={disabled}
        value={internalValue}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onChange={handleInputOnchange}
        onKeyDown={onKeyDown}
        fullWidth={props.fullWidth !== undefined ? props.fullWidth : true}
        placeholder={props.placeholder}
        type={type}
        InputProps={{
          autoComplete: "off",
          autoCorrect: "off",
          autoCapitalize: "off",
          spellCheck: "false",
          classes: { root: classNames("input-base") },
          ...InputProps,
        }}
      />
      <FieldError meta={meta} />
    </FormControl>
  );
};
